if (!customElements.get('gripb-void-slider')) {
  customElements.define('gripb-void-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (!this.track || !this.slides.length) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.visible = true;
      this.focused = false;
      this.hovered = false;
      this.dragging = false;
      this.counter = this.querySelector('[data-current]');
      this.toggle = this.querySelector('[data-toggle]');
      this.status = this.querySelector('[data-status]');
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true), {signal});
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true), {signal});
      this.toggle?.addEventListener('click', () => {
        this.paused = !this.paused;
        if (!this.paused) { this.focused = false; this.hovered = false; }
        this.updateToggle();
        this.schedule();
      }, {signal});
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
      }, {signal});
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, {signal});
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, {signal});
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      }, {signal});
      this.track.addEventListener('pointerdown', () => { this.dragging = true; this.schedule(); }, {signal, passive: true});
      window.addEventListener('pointerup', () => { this.dragging = false; this.schedule(); }, {signal, passive: true});
      window.addEventListener('pointercancel', () => { this.dragging = false; this.schedule(); }, {signal, passive: true});
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const next = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
          this.activate(next);
          this.schedule();
        });
      }, {signal, passive: true});
      this.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const target = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
        this.go(target, true);
      }, {signal});
      document.addEventListener('visibilitychange', () => this.schedule(), {signal});
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.updateToggle();
        this.schedule();
      }, {signal});
      this.addEventListener('shopify:block:select', event => {
        const selected = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId || slide.contains(event.target));
        if (selected < 0) return;
        this.paused = true;
        this.updateToggle();
        this.go(selected, true);
      }, {signal});
      this.trackWidth = this.track.clientWidth;
      this.resize = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (!width || width === this.trackWidth) return;
        this.trackWidth = width;
        this.track.scrollTo({left: this.index * width, behavior: 'instant'});
      });
      this.resize.observe(this.track);
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      }, {threshold: 0.1});
      this.intersection.observe(this);
      this.activate(0);
      this.updateToggle();
      this.schedule();
    }
    activate(index) {
      this.index = index;
      this.slides.forEach((slide, position) => {
        const active = position === index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelector('a')?.setAttribute('tabindex', active ? '0' : '-1');
      });
      if (this.counter) this.counter.textContent = String(index + 1);
    }
    async go(index, announce = false) {
      clearTimeout(this.timer);
      const target = (index + this.slides.length) % this.slides.length;
      const ticket = this.ticket = (this.ticket || 0) + 1;
      const image = this.slides[target].querySelector('img');
      if (image) {
        image.loading = 'eager';
        if (!image.complete) await image.decode().catch(() => {});
      }
      if (!this.isConnected || ticket !== this.ticket) return;
      this.track.scrollTo({left: target * this.track.clientWidth, behavior: this.motion.matches ? 'instant' : 'smooth'});
      if (announce && this.status) this.status.textContent = this.dataset.statusLabel.replace('[current]', String(target + 1));
      this.schedule();
    }
    updateToggle() {
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', String(this.paused));
      this.toggle.setAttribute('aria-label', this.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.toggle.querySelector('[data-pause-icon]').toggleAttribute('hidden', this.paused);
      this.toggle.querySelector('[data-play-icon]').toggleAttribute('hidden', !this.paused);
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.slides.length < 2 || this.paused || this.hovered || this.focused || this.dragging || !this.visible || document.hidden) return;
      this.timer = setTimeout(() => this.go(this.index + 1), Number(this.dataset.delay) || 6000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      this.ticket = (this.ticket || 0) + 1;
      this.abort?.abort();
      this.abort = null;
      this.resize?.disconnect();
      this.intersection?.disconnect();
    }
  });
}
